import type { AttributeMap } from 'quill/core';
import { Delta, Op, OpIterator } from 'quill/core';

const blockEmbeds = new Set([
  'attachment',
  'code-embed',
  'delayed-render-block-group',
  'embed',
  'card',
  'hr',
  'image',
  'iframe',
  'table-embed',
  'video',
  'button',
]);

const parseBlockEmbed = (op: Op): [null, null] | [string, unknown] => {
  if (op.insert && typeof op.insert === 'object') {
    const key = Object.keys(op.insert)[0];
    if (key && blockEmbeds.has(key)) {
      return [key, op.insert[key]];
    }
  }
  return [null, null];
};

export type RenderBlock =
  | {
      type: 'text';
      line: Delta;
      attributes: AttributeMap;
    }
  | {
      type: 'embed';
      key: string;
      value: unknown;
      attributes: AttributeMap;
    };

export const renderBlocksToDelta = (renderBlocks: RenderBlock[]) => {
  let delta = new Delta();
  renderBlocks.forEach((renderBlock) => {
    if (renderBlock.type === 'text') {
      delta = delta
        .concat(renderBlock.line)
        .insert('\n', renderBlock.attributes);
    } else {
      delta.insert(
        { [renderBlock.key]: renderBlock.value },
        renderBlock.attributes,
      );
    }
  });
  return delta;
};

const getRenderBlockIterator = (delta: Delta): Iterable<RenderBlock> => ({
  [Symbol.iterator]() {
    let unfulfilledLine = new Delta();
    const getAndResetUnfulfilledLine = (attributes: AttributeMap) => {
      const value: RenderBlock = {
        type: 'text',
        line: unfulfilledLine,
        attributes,
      };
      unfulfilledLine = new Delta();
      return value;
    };

    const iter = new OpIterator(delta.ops);
    return {
      next() {
        while (iter.hasNext()) {
          const op = iter.peek();
          if (!op.insert) break;

          const [blockEmbedKey, blockEmbedValue] = parseBlockEmbed(op);
          if (blockEmbedKey) {
            if (unfulfilledLine.length() > 0) break;
            iter.next();
            return {
              done: false,
              value: {
                type: 'embed',
                key: blockEmbedKey,
                value: blockEmbedValue,
                attributes: op.attributes || {},
              },
            };
          }

          const start = Op.length(op) - iter.peekLength();
          const newlineCharIndex =
            typeof op.insert === 'string'
              ? op.insert.indexOf('\n', start) - start
              : -1;
          if (newlineCharIndex === 0) {
            return {
              done: false,
              value: getAndResetUnfulfilledLine(iter.next(1).attributes || {}),
            };
          }

          unfulfilledLine.push(
            newlineCharIndex > 0 ? iter.next(newlineCharIndex) : iter.next(),
          );
        }
        return unfulfilledLine.length() > 0
          ? { done: false, value: getAndResetUnfulfilledLine({}) }
          : { done: true, value: undefined };
      },
    };
  },
});

const deltaToRenderBlocks = (delta: Delta) => [
  ...getRenderBlockIterator(delta),
];

export default deltaToRenderBlocks;
